import axios from 'axios';
import appConfig from '../../configs/app.config';
import { tokenStorage } from '../auth/tokenStorage';
import { globalToast } from '../toast/globalToast';

export function responseInterceptor(response) {
  return response.data;
}

let refreshPromise = null;

async function performRefresh() {
  const refreshToken = tokenStorage.getRefreshToken();
  if (!refreshToken) throw new Error('No refresh token');
  const res = await axios.post(
    `${appConfig.apiBaseUrl.replace(/\/$/, '')}/staff/auth/refresh`,
    { refresh_token: refreshToken },
    { headers: { 'Content-Type': 'application/json' } }
  );
  const data = res?.data?.data ?? res?.data;
  if (!data?.access_token) throw new Error('Refresh failed');
  tokenStorage.setAccessToken(data.access_token);
  if (data.refresh_token) tokenStorage.setRefreshToken(data.refresh_token);
  return data.access_token;
}

function clearAndRedirect(delayMs = 2500) {
  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    setTimeout(() => {
      tokenStorage.clear();
      window.location.replace('/login');
    }, delayMs);
  }
}

export function createResponseErrorInterceptor(client) {
  return async function responseErrorInterceptor(error) {
    const { response, config } = error;
    const originalRequest = config || {};
    const url = (originalRequest.url || '').toLowerCase();
    const isAuthEndpoint =
      url.includes('/staff/auth/login') || url.includes('/staff/auth/refresh');

    // Opt-out: callers can mark a request as non-critical so a 401 here will
    // NOT attempt token refresh and will NOT force a logout/redirect.
    if (response?.status === 401 && originalRequest.skipAuthRedirect) {
      return Promise.reject(error);
    }

    if (response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;
      try {
        if (!refreshPromise) {
          refreshPromise = performRefresh().finally(() => {
            refreshPromise = null;
          });
        }
        const newToken = await refreshPromise;
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        originalRequest.headers.access_token = newToken;
        return client(originalRequest);
      } catch (refreshError) {
        globalToast.error('Your session has expired. Please log in again.', 'Session Expired');
        clearAndRedirect();
        return Promise.reject(refreshError);
      }
    }

    if (response?.status === 401) {
      globalToast.error('Unauthorized. Please log in again.', 'Authentication Error');
      clearAndRedirect();
    }
    return Promise.reject(error);
  };
}
